import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { jobsAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import { TrendingUp, Users, Clock, Award } from 'lucide-react';

const Container = styled.div`
  display: grid;
  gap: 1.5rem;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Heading = styled.h2`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${p => p.theme.borderRadius.full};
  background: ${p => p.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.primary};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${p => p.theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const Section = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${p => p.theme.colors.text.primary};
`;

const StatusChart = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const StatusItem = styled.div`
  padding: 1rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
  text-align: center;
`;

const StatusCount = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${p => p.theme.colors.primary};
`;

const StatusLabel = styled.div`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 0.5rem;
`;

const JobsList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const JobItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
`;

const JobTitle = styled.div`
  font-weight: 500;
  color: ${p => p.theme.colors.text.primary};
`;

const JobCount = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${p => p.theme.colors.primary};
`;

const Loading = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${p => p.theme.colors.text.secondary};
`;

const JobApplicationsDashboard = () => {
  const { theme } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const res = await jobsAPI.getStatistics({});
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <Loading theme={theme}>Loading dashboard...</Loading>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container theme={theme}>
        <Loading theme={theme}>No data available</Loading>
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Heading theme={theme}>Job Applications Dashboard</Heading>
      </Header>

      <StatsGrid>
        <StatCard theme={theme}>
          <StatIcon theme={theme}>
            <Users size={24} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{stats.total_applications || 0}</StatValue>
            <StatLabel theme={theme}>Total Applications</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard theme={theme}>
          <StatIcon theme={theme}>
            <Award size={24} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{stats.average_rating || 'N/A'}</StatValue>
            <StatLabel theme={theme}>Average Rating</StatLabel>
          </StatContent>
        </StatCard>

        {stats.average_time_to_hire && (
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <Clock size={24} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.average_time_to_hire} days</StatValue>
              <StatLabel theme={theme}>Avg. Time to Hire</StatLabel>
            </StatContent>
          </StatCard>
        )}

        {stats.average_time_to_rejection && (
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <TrendingUp size={24} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.average_time_to_rejection} days</StatValue>
              <StatLabel theme={theme}>Avg. Time to Rejection</StatLabel>
            </StatContent>
          </StatCard>
        )}
      </StatsGrid>

      {stats.status_breakdown && stats.status_breakdown.length > 0 && (
        <Section theme={theme}>
          <SectionTitle theme={theme}>Applications by Status</SectionTitle>
          <StatusChart>
            {stats.status_breakdown.map((status) => (
              <StatusItem key={status.status} theme={theme}>
                <StatusCount theme={theme}>{status.count}</StatusCount>
                <StatusLabel theme={theme}>{status.label}</StatusLabel>
              </StatusItem>
            ))}
          </StatusChart>
        </Section>
      )}

      {stats.applications_per_job && stats.applications_per_job.length > 0 && (
        <Section theme={theme}>
          <SectionTitle theme={theme}>Applications per Job</SectionTitle>
          <JobsList>
            {stats.applications_per_job.map((job) => (
              <JobItem key={job.job_id} theme={theme}>
                <JobTitle theme={theme}>{job.job_title}</JobTitle>
                <JobCount theme={theme}>{job.applications_count}</JobCount>
              </JobItem>
            ))}
          </JobsList>
        </Section>
      )}
    </Container>
  );
};

export default JobApplicationsDashboard;














